import React, { useEffect, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
} from "@mui/material";
import { StatusChip, StatusIndicator } from "../ui/index";
import {
  ArrowUpRightIcon,
  CalenderDateIcon,
  DownArrowIcons,
  EditIcon,
  ViewsIcon,
} from "../global/Icons";

function DefaultActions({ actions, row }) {
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {actions?.view && (
        <IconButton
          onClick={() => actions.view(row)}
          size="small"
          sx={{
            p: 1,
            bgcolor: (theme) => theme.palette.primary.lighter,
            borderRadius: 8,
          }}
        >
          <ViewsIcon />
        </IconButton>
      )}
      {actions?.edit && (
        <IconButton
          onClick={() => actions.edit(row)}
          size="small"
          sx={{
            p: 1,
            bgcolor: (theme) => theme.palette.primary.lighter,
            borderRadius: 8,
          }}
        >
          <EditIcon />
        </IconButton>
      )}
    </Box>
  );
}

function SortArrow({ active, show, sortOrder }) {
  return (
    <IconButton
      size="small"
      className="sortArrow"
      sx={{
        ml: 1,
        p: 0,
        color: "#283745",
        fontSize: "16px",
        pointerEvents: "none",
        transform: active && sortOrder === "desc" ? "rotate(180deg)" : "none",
        opacity: show ? 1 : 0,
        visibility: show ? "visible" : "hidden",
        transition: "opacity 150ms ease",
      }}
    >
      <DownArrowIcons />
    </IconButton>
  );
}

const COLUMN_WIDTHS = {
  actions: 120,
  firstData: 220,
  default: 180,
  status: 140,
  createdBy: 180,
  engagements: 220,
  referredFrom: 200,
};

const clampSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  display: "block",
};

function DataTable({
  columns = [],
  rows = [],
  actions,
  loading = false,
  emptyMessage = "No data found.",
  sx,
  sortableHeaders = [],
  sortBy,
  sortOrder,
  onSortRequest,
  activeSortHeader,
  onEndReached,
  hasMore = false,
  isFetchingMore = false,
}) {
  const scrollerRef = useRef(null);

  const ACTIONS_COLUMN_WIDTH = COLUMN_WIDTHS.actions;
  const FIRST_DATA_COLUMN_WIDTH = COLUMN_WIDTHS.firstData;

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (loading || isFetchingMore || !hasMore || !rows?.length) return;
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollHeight <= clientHeight) return;
      if (scrollTop + clientHeight >= scrollHeight - 48) {
        onEndReached?.();
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [onEndReached, hasMore, rows?.length, loading, isFetchingMore]);

  const getColumnWidth = (col, idx) => {
    if (idx === 0) return FIRST_DATA_COLUMN_WIDTH;
    if (col.type === "status") return COLUMN_WIDTHS.status;
    if (col.type === "createdBy") return COLUMN_WIDTHS.createdBy;
    if (col.field === "engagements") return COLUMN_WIDTHS.engagements;
    if (col.field === "referredFrom") return COLUMN_WIDTHS.referredFrom;
    return COLUMN_WIDTHS.default;
  };

  const renderCellContent = (col, row, value) => {
    if (col.render) return col.render(row);

    if (col.type === "status") {
      if (value === "" || value == null) {
        return (
          <Typography
            sx={{
              fontSize: 13,
              color: "#374151",
              display: "inline-block",
              minWidth: 32,
              textAlign: "center",
            }}
          >
            -
          </Typography>
        );
      }
      if (value === "KYC Approved")
        return <StatusIndicator variant="approved" label={value} />;
      if (value === "Submitted")
        return <StatusChip label={value} variant="submit" />;
      if (value === "Not Approved")
        return <StatusChip label={value} variant="notApproved" />;
      if (value === "Pending")
        return <StatusChip label={value} variant="pending" />;
      return <StatusChip label={value} variant="review" />;
    }

    if (col.type === "createdBy" && value) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "start",
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            <Avatar
              src={value.avatar}
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                bgcolor: "#E5E7EB",
                color: "#6B7280",
                fontSize: 12,
                fontWeight: 600,
                border: "2px solid #D6D9DB",
              }}
            >
              {value.name?.charAt(0)}
            </Avatar>
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 400,
                color: "#292C2F",
                lineHeight: 1.3,
                ...clampSx,
                maxWidth: 120,
              }}
            >
              {value.name}
            </Typography>
          </Box>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}
          >
            <CalenderDateIcon />
            <Typography
              sx={{
                fontSize: 12,
                color: "#292C2F",
                lineHeight: 1,
                ...clampSx,
                maxWidth: 120,
              }}
            >
              {value.date}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (col.field === "engagements") {
      if (!Array.isArray(value) || value.length === 0) {
        return (
          <Typography sx={{ fontSize: 13, color: "#374151" }}>-</Typography>
        );
      }

      const tags = value.map((e) => e.name).filter(Boolean);

      return (
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            flexWrap: "nowrap",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {tags.slice(0, 2).map((tag, idx) => (
            <Chip
              key={idx}
              label={tag}
              size="small"
              sx={{
                height: 24,
                fontSize: 12,
                borderRadius: "4px",
                fontWeight: 600,
                bgcolor: "#E7E8EA",
                color: "#292C2F",
                border: "1px solid #E5E7EB",
                maxWidth: 90,
                "& .MuiChip-label": {
                  py: 0.5,
                  px: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "block",
                },
              }}
            />
          ))}
          {tags.length > 2 && (
            <Chip
              label={`+${tags.length - 2}`}
              size="small"
              sx={{
                flexShrink: 0,
                borderRadius: "4px",
                height: 24,
                fontSize: 12,
                fontWeight: 600,
                bgcolor: "#E7E8EA",
                color: "#292C2F",
                border: "1px solid #E5E7EB",
                "& .MuiChip-label": { py: 0.5, px: 1 },
              }}
            />
          )}
        </Box>
      );
    }

    if (col.field === "referredFrom") {
      const hasProposal = value && value.includes("Approved proposal");
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            overflow: "hidden",
          }}
        >
          {hasProposal && (
            <IconButton size="small" sx={{ flexShrink: 0 }}>
              <ArrowUpRightIcon />
            </IconButton>
          )}
          <Typography
            sx={{
              fontSize: 14,
              color: "#292C2F",
              textDecoration: hasProposal ? "underline" : "none",
              ...clampSx,
            }}
          >
            {value}
          </Typography>
        </Box>
      );
    }

    if (col.type === "bold") {
      return (
        <Typography
          sx={{ fontSize: 14, fontWeight: 400, color: "#292C2F", ...clampSx }}
        >
          {value}
        </Typography>
      );
    }

    return (
      <Typography sx={{ fontSize: 13, color: "#374151", ...clampSx }}>
        {value}
      </Typography>
    );
  };

  const isSortable = (col) => {
    if (typeof col.sortable === "boolean") return col.sortable;
    if (Array.isArray(sortableHeaders) && sortableHeaders.length > 0) {
      return (
        sortableHeaders.includes(col.header) ||
        sortableHeaders.includes(col.field)
      );
    }
    return false;
  };

  const isActiveSort = (col) => {
    if (activeSortHeader)
      return activeSortHeader === col.header || activeSortHeader === col.field;
    if (sortBy) return sortBy === col.header || sortBy === col.field;
    return false;
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        ...sx,
      }}
    >
      <TableContainer
        ref={scrollerRef}
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 0,
          boxShadow: "none",
          border: "1px solid #E5E7EB",
          background: "#FFFFFF",
          width: "100%",
          maxHeight: "calc(100vh - 400px)",
          overflowX: "auto",
          overflowY: "auto",
        }}
      >
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            minWidth: 900,
          }}
          size="small"
          stickyHeader
        >
          <colgroup>
            {actions && <col style={{ width: ACTIONS_COLUMN_WIDTH }} />}
            {columns.map((col, idx) => (
              <col
                key={col.field || idx}
                style={{ width: getColumnWidth(col, idx) }}
              />
            ))}
          </colgroup>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: "#E7E8EA",
                "& th": { borderBottom: "1px solid #E5E7EB" },
              }}
            >
              {actions && (
                <TableCell
                  sx={{
                    fontFamily: "Inter",
                    fontWeight: 600,
                    color: "#292C2F",
                    fontSize: "14px",
                    lineHeight: "147%",
                    letterSpacing: "-0.75%",
                    padding: "14px 16px",
                    whiteSpace: "nowrap",
                    bgcolor: "#E7E8EA",
                    borderBottom: "1px solid #E5E7EB",
                    verticalAlign: "middle",
                    position: "sticky",
                    left: 0,
                    zIndex: 4,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                    overflow: "hidden",
                  }}
                >
                  Actions
                </TableCell>
              )}

              {columns.map((col, idx) => {
                const sortable = isSortable(col);
                const isActive = isActiveSort(col);
                return (
                  <TableCell
                    key={col.field || col.header || idx}
                    onClick={() =>
                      sortable && typeof onSortRequest === "function"
                        ? onSortRequest(col.field || col.header)
                        : undefined
                    }
                    sx={{
                      fontFamily: "Inter",
                      fontWeight: 600,
                      color: "#292C2F",
                      fontSize: "14px",
                      lineHeight: "147%",
                      letterSpacing: "-0.75%",
                      padding: "14px 16px",
                      whiteSpace: "nowrap",
                      bgcolor: "#E7E8EA",
                      borderBottom: "1px solid #E5E7EB",
                      verticalAlign: "middle",
                      cursor: sortable ? "pointer" : "default",
                      userSelect: sortable ? "none" : "auto",
                      overflow: "hidden",
                      "&:hover .sortArrow": {
                        opacity: 1,
                        visibility: "visible",
                      },
                      ...(idx === 0 && {
                        position: "sticky",
                        left: actions ? ACTIONS_COLUMN_WIDTH : 0,
                        zIndex: 4,
                        borderRight: "2px solid rgba(0,0,0,0.05)",
                      }),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      {sortable && (
                        <SortArrow
                          active={isActive}
                          show={isActive}
                          sortOrder={sortOrder}
                        />
                      )}
                      {col.header}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  align="center"
                  sx={{ border: "none", py: 6 }}
                >
                  <CircularProgress size={40} color="primary" />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (actions ? 1 : 0)}
                  align="center"
                  sx={{ border: "none", py: 6 }}
                >
                  <Typography variant="body2" color="textSecondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rows.map((row, rowIdx) => (
                  <TableRow
                    key={row.id || rowIdx}
                    sx={{
                      "&:hover": { bgcolor: "#F9FAFB" },
                      "& td": {
                        borderBottom:
                          rowIdx === rows.length - 1
                            ? "none"
                            : "1px solid #F3F4F6",
                      },
                    }}
                  >
                    {actions && (
                      <TableCell
                        sx={{
                          padding: "16px",
                          position: "sticky",
                          left: 0,
                          zIndex: 3,
                          bgcolor: "#FFFFFF",
                          overflow: "hidden",
                        }}
                      >
                        <DefaultActions actions={actions} row={row} />
                      </TableCell>
                    )}

                    {columns.map((col, colIdx) => (
                      <TableCell
                        key={colIdx}
                        sx={{
                          padding: "16px",
                          verticalAlign: "middle",
                          overflow: "hidden",
                          maxWidth: 0,
                          ...(colIdx === 0 && {
                            position: "sticky",
                            left: actions ? ACTIONS_COLUMN_WIDTH : 0,
                            zIndex: 3,
                            bgcolor: "#FFFFFF",
                            borderRight: "2px solid rgba(0,0,0,0.05)",
                          }),
                        }}
                      >
                        {renderCellContent(col, row, row[col.field])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {hasMore && rows.length > 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + (actions ? 1 : 0)}
                      align="center"
                      sx={{ border: "none", py: 2 }}
                    >
                      {isFetchingMore && (
                        <CircularProgress size={24} color="primary" />
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default DataTable;
